import type { BookingPersonality } from '@tilly/shared';
import {
  colors as baseColors,
  fonts as baseFonts,
  radius as baseRadius,
  spacing,
  fontWeights,
} from './tokens';

type ThemeColors = { [K in keyof typeof baseColors]: string };
type ThemeFonts = { [K in keyof typeof baseFonts]: string };
type ThemeRadius = { [K in keyof typeof baseRadius]: string };

export type BookingThemeTokens = {
  colors: ThemeColors;
  fonts: ThemeFonts;
  radius: ThemeRadius;
  spacing: typeof spacing;
  fontWeights: typeof fontWeights;
};

export interface BookingPersonalityMeta {
  id: BookingPersonality;
  label: string;
  description: string;
  swatches: [string, string, string];
}

interface BookingPersonalityConfig {
  meta: BookingPersonalityMeta;
  overrides: {
    colors?: Partial<ThemeColors>;
    fonts?: Partial<ThemeFonts>;
    radius?: Partial<ThemeRadius>;
  };
}

const configs: Record<BookingPersonality, BookingPersonalityConfig> = {
  classic: {
    meta: {
      id: 'classic',
      label: 'Tilly Classic',
      description: 'Natural and balanced with soft sage and brass accents.',
      swatches: ['#7FA897', '#F6F7F5', '#C9A46B'],
    },
    overrides: {},
  },
  tech: {
    meta: {
      id: 'tech',
      label: 'Tech',
      description: 'Dark interface with crisp contrast and restrained highlights.',
      swatches: ['#5FB9FF', '#0F141A', '#1A222C'],
    },
    overrides: {
      colors: {
        bg: '#0F141A',
        surface: '#141B23',
        surfaceRaised: '#1A222C',
        border: '#2C3A47',
        textPrimary: '#EAF1F7',
        textSecondary: '#B8C5D1',
        textMuted: '#93A4B3',
        accent: '#5FB9FF',
        accentHover: '#45A9F5',
        accentActive: '#2F97E0',
        accentLight: '#182B3A',
        accentLighter: '#11202D',
        brass: '#6AC4C0',
        brassHover: '#52B0AB',
        brassLight: '#213836',
        success: '#53C49A',
        warning: '#E6BE65',
        danger: '#F08C8C',
      },
      fonts: {
        heading: '"Sora", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      },
      radius: {
        sm: '8px',
        md: '10px',
        lg: '14px',
        xl: '18px',
      },
    },
  },
  beauty: {
    meta: {
      id: 'beauty',
      label: 'Beauty',
      description: 'Soft pink glow with bolder accents and editorial contrast.',
      swatches: ['#D66A93', '#FFF6FA', '#F4B4C9'],
    },
    overrides: {
      colors: {
        bg: '#FFF6FA',
        surface: '#FFFFFF',
        surfaceRaised: '#FEEEF5',
        border: '#F0CEDD',
        textPrimary: '#3A2431',
        textSecondary: '#6E4A5D',
        textMuted: '#9C7086',
        accent: '#D66A93',
        accentHover: '#C45682',
        accentActive: '#A9436D',
        accentLight: '#FBE0EA',
        accentLighter: '#FDECF2',
        brass: '#E88BAB',
        brassHover: '#D97899',
        brassLight: '#F6C8DA',
        success: '#5FAF8E',
        warning: '#DE9F51',
        danger: '#D86A88',
      },
      fonts: {
        heading: '"Fraunces", Georgia, serif',
      },
      radius: {
        sm: '12px',
        md: '16px',
        lg: '22px',
        xl: '26px',
      },
    },
  },
  clean: {
    meta: {
      id: 'clean',
      label: 'Clean',
      description: 'Minimal white canvas with subtle grayscale structure.',
      swatches: ['#4E7CA8', '#FFFFFF', '#E9EEF2'],
    },
    overrides: {
      colors: {
        bg: '#FFFFFF',
        surface: '#FFFFFF',
        surfaceRaised: '#F7F9FB',
        border: '#E2E8EE',
        textPrimary: '#1F2933',
        textSecondary: '#4A5866',
        textMuted: '#6D7B89',
        accent: '#4E7CA8',
        accentHover: '#3C6A95',
        accentActive: '#2F587D',
        accentLight: '#E8F0F8',
        accentLighter: '#F2F7FC',
        brass: '#8AA5BF',
        brassHover: '#7894AF',
        brassLight: '#D6E2EE',
      },
      fonts: {
        heading: '"IBM Plex Serif", Georgia, serif',
        body: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      },
      radius: {
        sm: '6px',
        md: '8px',
        lg: '12px',
        xl: '16px',
      },
    },
  },
  bb: {
    meta: {
      id: 'bb',
      label: 'B&B',
      description: 'Cozy lodge feel with warm paper tones and handcrafted accents.',
      swatches: ['#8D6849', '#F7F1E7', '#B8895D'],
    },
    overrides: {
      colors: {
        bg: '#F7F1E7',
        surface: '#FFF8EE',
        surfaceRaised: '#F1E6D6',
        border: '#DCCAB4',
        textPrimary: '#3D2F24',
        textSecondary: '#6C5441',
        textMuted: '#8C725D',
        accent: '#8D6849',
        accentHover: '#7B593E',
        accentActive: '#684A33',
        accentLight: '#EBDDCB',
        accentLighter: '#F2E8DA',
        brass: '#B8895D',
        brassHover: '#A7784D',
        brassLight: '#E3C7AA',
        success: '#6FA07D',
        warning: '#D89A4C',
        danger: '#C9756B',
      },
      fonts: {
        heading: '"Bitter", Georgia, serif',
        body: '"Source Sans 3", -apple-system, BlinkMacSystemFont, sans-serif',
      },
      radius: {
        sm: '8px',
        md: '12px',
        lg: '20px',
        xl: '24px',
      },
    },
  },
  adventure: {
    meta: {
      id: 'adventure',
      label: 'Adventure',
      description: 'Playful and energetic for family-friendly bookings.',
      swatches: ['#2B90D9', '#F5FBFF', '#F5B335'],
    },
    overrides: {
      colors: {
        bg: '#F5FBFF',
        surface: '#FFFFFF',
        surfaceRaised: '#E9F6FF',
        border: '#C9E5F6',
        textPrimary: '#16324A',
        textSecondary: '#2F5570',
        textMuted: '#4F738C',
        accent: '#2B90D9',
        accentHover: '#1F7FC4',
        accentActive: '#166DAA',
        accentLight: '#D7EEFB',
        accentLighter: '#ECF7FF',
        brass: '#F5B335',
        brassHover: '#E1A028',
        brassLight: '#F9DDA4',
        success: '#42B883',
        warning: '#F39C34',
        danger: '#E57373',
      },
      fonts: {
        heading: '"Baloo 2", "Nunito", -apple-system, BlinkMacSystemFont, sans-serif',
        body: '"Nunito", -apple-system, BlinkMacSystemFont, sans-serif',
      },
      radius: {
        sm: '12px',
        md: '16px',
        lg: '24px',
        xl: '28px',
      },
    },
  },
  dark: {
    meta: {
      id: 'dark',
      label: 'Dark',
      description: 'Deep and moody with jewel-toned violet and crimson — built for bold studios.',
      swatches: ['#9D4EDD', '#0C0C0E', '#C4364A'],
    },
    overrides: {
      colors: {
        bg: '#0C0C0E',
        surface: '#131316',
        surfaceRaised: '#1A1A1F',
        border: '#2A2A35',
        textPrimary: '#F0EDF8',
        textSecondary: '#B8B0D0',
        textMuted: '#7A7292',
        accent: '#9D4EDD',
        accentHover: '#8B3ECD',
        accentActive: '#7A2EBD',
        accentLight: '#2A1A40',
        accentLighter: '#1E1230',
        brass: '#C4364A',
        brassHover: '#AD2E40',
        brassLight: '#3D1020',
        success: '#5BA88C',
        warning: '#D4A017',
        danger: '#F55566',
      },
      fonts: {
        heading: '"Cinzel", "Trajan Pro", Georgia, serif',
        body: '"Raleway", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      },
      radius: {
        sm: '4px',
        md: '6px',
        lg: '10px',
        xl: '14px',
      },
    },
  },
  elegant: {
    meta: {
      id: 'elegant',
      label: 'Elegant',
      description: 'Art Deco luxury — black, ivory, and bold geometric gold.',
      swatches: ['#D4AF37', '#0A0908', '#F5F0E0'],
    },
    overrides: {
      colors: {
        bg: '#0A0908',
        surface: '#161410',
        surfaceRaised: '#1E1C14',
        border: '#33301E',
        textPrimary: '#F5F0E0',
        textSecondary: '#C8B87A',
        textMuted: '#8A7A4A',
        accent: '#D4AF37',
        accentHover: '#C49A2A',
        accentActive: '#B08820',
        accentLight: '#2A240C',
        accentLighter: '#1E1A08',
        brass: '#D4AF37',
        brassHover: '#C49A2A',
        brassLight: '#3A3010',
        success: '#7AA87A',
        warning: '#E6C445',
        danger: '#E06060',
      },
      fonts: {
        heading: '"Playfair Display", "Didot", Georgia, serif',
        body: '"Josefin Sans", "Gill Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      },
      radius: {
        sm: '2px',
        md: '4px',
        lg: '6px',
        xl: '8px',
      },
    },
  },
};

export function resolveBookingTokens(
  personality: BookingPersonality | undefined
): BookingThemeTokens {
  const normalized = personality ?? 'classic';
  const config = configs[normalized] ?? configs.classic;

  return {
    colors: {
      ...baseColors,
      ...config.overrides.colors,
    },
    fonts: {
      ...baseFonts,
      ...config.overrides.fonts,
    },
    radius: {
      ...baseRadius,
      ...config.overrides.radius,
    },
    spacing,
    fontWeights,
  };
}

export const bookingPersonalityOptions: BookingPersonalityMeta[] = [
  configs.classic.meta,
  configs.tech.meta,
  configs.beauty.meta,
  configs.clean.meta,
  configs.bb.meta,
  configs.adventure.meta,
  configs.dark.meta,
  configs.elegant.meta,
];
